import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import CategoryHeader from '../../common/components/CategoryHeader';
import { Category } from '@/graphql/types/Category';
import { openLocalizationRail } from '@/localization/actions';
import NationalMenuTile from '../categories/national/NationalMenuTile';
import { CategoryImages } from '@/common/pageSetup/categories/nationalMenuData/types';
import nationalMenuData from '@/common/pageSetup/categories/nationalMenuData';
import useStyles from '../styles';
import {
  onMenuCategoryClick,
  onMenuCategoryPageLoad,
  onNationalClickGetStarted
} from '@/dataAnalytics/dataAnalyticsHelper';
import { useApplicationInitialized } from '@/hooks/useApplicationInitialized';
import { useHandleSuccessMenuLoadAfterError } from '@/hooks/useErrorHandling';
import useAnalytics from '@/dataAnalytics/hooks/useAnalytics';
import { useCCGetCategoriesQuery } from '@/clientCore/temporaryTransformationalHooks/useCCGetCategoriesQuery';
import { localizationSelectors } from '@/localization/localizationSelectors';
import { CCOccasionChoicesTransformer, CCOccasionPicker } from '@/clientCore/temporaryTransformationalHooks/types';

interface CategoriesPageProps {
  isLocalized: boolean;
}

const ALPHA_NUMERIC_REGEX = /[^a-zA-Z0-9]/g;

const getCategoryIDFromName = (categoryName: string): string => categoryName.split('/').pop() ?? '';

const CategoriesPage = ({
  isLocalized
}: CategoriesPageProps): JSX.Element => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const analytics = useAnalytics();
  const router = useRouter();
  const isAppInitialized = useApplicationInitialized();
  const successMenuLoadAfterRedirectError = useHandleSuccessMenuLoadAfterError();
  const storeDetails = useSelector(localizationSelectors.storeDetails);
  const occasion: OccasionChoice = useSelector(localizationSelectors.occasion);

  const formattedOccasion = CCOccasionPicker[occasion as keyof CCOccasionChoicesTransformer];

  const { data: ccData, loading } = useCCGetCategoriesQuery({
    occasion: formattedOccasion,
    storeNumber: storeDetails?.storeNumber ?? '000002',
    isMainMenu: true
  });

  const categories = (ccData as Category[]);

  useEffect(() => {
    if (isAppInitialized) {
      analytics.push(() => onMenuCategoryPageLoad(isLocalized));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLocalized, isAppInitialized]);

  /*
   * Here the Menu categories has loaded
   */
  successMenuLoadAfterRedirectError(!loading);

  const handleTileClick = (categoryName?: string): void => {
    // Converts category name to lowercase and removes special characters.
    const categoryID = getCategoryIDFromName(categoryName as string)?.toLowerCase().replace(ALPHA_NUMERIC_REGEX, '');

    analytics.push(() => onMenuCategoryClick(categoryID, isLocalized));

    if (isLocalized) {
      router.push(`/menu/${categoryID}`);
    } else {
      dispatch(openLocalizationRail());
    }
  };

  const handleCtaClick = () => {
    analytics.push(() => onNationalClickGetStarted());
    dispatch(openLocalizationRail());
  };

  const nationalData = nationalMenuData('category') as CategoryImages[];

  const testId = isLocalized
    ? 'localized-menu-category-tile'
    : 'national-menu-category-tile';

  const formImageURL = (categoryName: string, isMobile:boolean): string => {
    const imageName = categoryName.toLowerCase().split(' ').join('_');
    return isMobile
      ? `/images/nationalMenu/menu/mobile/${imageName}.png`
      : `/images/nationalMenu/menu/desktop/${imageName}.png`;
  };

  return (
    <>
      <CategoryHeader
        loadingCategoryName={false}
        pageDisplayName="Menu"
        handleCTAClick={handleCtaClick}
        isLocalized={isLocalized}
      />
      {categories?.length ? (
        <ul className={`${classes.menu} ${classes.content}`}>
          {categories?.map((category) => {
            const image = {
              desktop:
                nationalData.find((dataItem) => category.name.includes(dataItem.id))
                  ?.mainImage || formImageURL(category.name, false),
              mobile:
                nationalData.find((dataItem) => category.name.includes(dataItem.id))
                  ?.mainImage || formImageURL(category.name, true),
              altText:
                category?.mainImage?.altText || category.displayName || ''
            };

            return (
              <li
                className={classes.nationalMenuTile}
                data-testid={testId}
                key={category.name}
              >
                <NationalMenuTile
                  image={image}
                  id={category.name}
                  name={category.displayName ?? ''}
                  handleClick={handleTileClick}
                />
              </li>
            );
          })}
        </ul>
      ) : (
        <></>
      )}
    </>
  );
};

export default CategoriesPage;
